<template>
  <div>
    <v-flex>
      <v-container>
        <v-row align="center">
          <v-col>
            <h1 class="font-weight-bold mb-2 mt-2 d-flex align-center">
              <v-icon class="mr-3" large>mdi-movie-open</v-icon>
              <span>Studio</span>
            </h1>
          </v-col>
          <v-col cols="2" class="text-right">
            <v-menu
              offset-y
              max-width="200"
              min-width="200"
              :close-on-click="true"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{on}">
                <v-btn v-on="on">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  link
                  @click="showDetailDialog(studio)"
                  :loading="studio.loading"
                >
                  <v-list-item-title>Thumbnail</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="studio.permission === 'owner'"
                  link
                  @click="setShareDialog(true)"
                  :disabled="pointClouds.length < 1"
                >
                  <v-list-item-title>Share</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="studio.permission === 'owner'"
                  link
                  @click="archive"
                >
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
                <v-list-item
                  link
                  @click="setProfileDialog(true)"
                  v-if="$route.name !== 'studios_new'"
                >
                  <v-list-item-title>Owner</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <v-card-text v-if="loading">
          Now loading...
          <v-progress-linear
            indeterminate
            color="primary"
            class="mb-10"
          ></v-progress-linear>
        </v-card-text>
        <template v-if="!loading">
          <h3 v-if="brainDetailsNotAvailable.length > 0" class="red--text">
            This studio is broken.
          </h3>
          <h3
            v-for="brainDetailNotAvailable in brainDetailsNotAvailable"
            :key="brainDetailNotAvailable"
            class="red--text"
          >
            {{ brainDetailNotAvailable }}
          </h3>
          <v-row>
            <v-col cols="2" class="font-weight-bold">
              Title
            </v-col>
            <v-col cols="5">
              <v-form v-model="studio.title.valid" @submit.prevent>
                <v-text-field
                  name="studio-title"
                  hide-details="auto"
                  v-model="studio.title.value"
                  :rules="studio.title.rules"
                  outlined
                  dense
                  :readonly="!studio.title.editable"
                  :filled="!studio.title.editable"
                  max="15"
                ></v-text-field>
              </v-form>
            </v-col>
            <v-col cols="1" v-if="!studio.title.editable && hasWritePermission">
              <v-btn text @click="studio.title.editable = true">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="1" v-if="studio.title.editable">
              <v-btn
                :disabled="!studio.title.valid"
                text
                @click="updateStudioTitle"
              >
                <v-icon color="success">mdi-check</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="2">
              <h3 v-if="studio.permission === 'r'" class="blue--text mt-4">
                Read only
              </h3>
              <h3 v-if="studio.permission === 'rw'" class="blue--text mt-4">
                Read and write
              </h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2" />
            <v-col>
              <v-btn
                :href="'/studios/viewer/' + studio.id"
                target="_blank"
                rel="noopener"
                color="primary"
                class="text-none"
                :disabled="
                  pointClouds.length < 1 || brainDetailsNotAvailable.length > 0
                "
              >
                Launch viewer
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2" class="font-weight-bold">
              Notes
            </v-col>
            <v-col cols="6" v-if="!studio.notes.editable">
              <markdown-it-vue
                v-if="!is_empty(studio.notes.value)"
                class="md-body"
                :content="$sanitize(studio.notes.value)"
              />
            </v-col>
            <v-col cols="6" v-else>
              <v-form v-model="studio.notes.valid" @submit.prevent>
                <v-textarea
                  outlined
                  v-model="studio.notes.value"
                  name="studio-notes"
                  :rules="studio.notes.rules"
                  label="Notes"
                />
              </v-form>
            </v-col>
            <v-col cols="4" v-if="!studio.notes.editable">
              <v-btn
                v-if="hasWritePermission"
                text
                @click="studio.notes.editable = true"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="4" v-else>
              <v-btn
                :disabled="!studio.notes.valid"
                text
                @click="updateStudioNotes"
              >
                <v-icon color="success">mdi-check</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="studio.shared || studio.publicAccess">
            <v-col class="red--text">
              The brains of the shared studio aren't editable.
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="2">
              <v-btn
                @click="setSelectBrainDialog(true)"
                :disabled="studio.shared || studio.publicAccess"
                color="primary"
                block
                class="text-none mt-5"
              >
                + Add brain...
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              v-for="(pointCloud, index) in pointClouds"
              :key="pointCloud.brainId + '_' + index"
              :cols="$vuetify.breakpoint.smAndUp ? 6 : 12"
            >
              <v-card>
                <v-card-title>
                  {{ pointCloud.title }}
                </v-card-title>
                <v-card-subtitle>id : {{ pointCloud.brainId }}</v-card-subtitle>
                <v-list-item class="px-6">
                  <v-list-item-content>
                    <v-row class="mr-3">
                      <v-col cols="5">Point coloring</v-col>
                      <v-col cols="5">
                        <v-select
                          v-model="pointCloud.pointColorType.value"
                          :items="['INTENSITY', 'COLOR']"
                          :readonly="!pointCloud.pointColorType.editable"
                        ></v-select>
                      </v-col>
                      <v-col v-if="hasWritePermission" cols="2">
                        <v-btn
                          v-if="!pointCloud.pointColorType.editable"
                          text
                          color="success"
                          @click="pointCloud.pointColorType.editable = true"
                        >
                          EDIT
                        </v-btn>
                        <v-btn
                          v-else
                          text
                          @click="
                            updatePointCloud(
                              index,
                              'PointColorType',
                              pointCloud.pointColorType
                            )
                          "
                        >
                          <v-icon color="success">mdi-check</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row class="mr-3">
                      <v-col cols="2">Color</v-col>
                      <v-col cols="8">
                        <v-color-picker
                          v-model="pointCloud.pointColor.value"
                          :disabled="!pointCloud.pointColor.editable"
                          flat
                        ></v-color-picker>
                      </v-col>
                      <v-col v-if="hasWritePermission" cols="2">
                        <v-btn
                          v-if="!pointCloud.pointColor.editable"
                          text
                          color="success"
                          @click="pointCloud.pointColor.editable = true"
                        >
                          EDIT
                        </v-btn>
                        <v-btn
                          v-else
                          text
                          @click="
                            updatePointCloud(
                              index,
                              'PointColor',
                              pointCloud.pointColor
                            )
                          "
                        >
                          <v-icon color="success">mdi-check</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row class="mr-3">
                      <v-col cols="5">Intensity (min)</v-col>
                      <v-col cols="5">
                        <v-form
                          v-model="pointCloud.intensityMin.valid"
                          @submit.prevent
                        >
                          <v-text-field
                            :name="'intensity-min-' + index"
                            hide-details="auto"
                            :readonly="!pointCloud.intensityMin.editable"
                            v-model="pointCloud.intensityMin.value"
                            :rules="pointCloud.intensityMin.rules"
                            outlined
                            dense
                            max="15"
                          ></v-text-field>
                        </v-form>
                      </v-col>
                      <v-col v-if="hasWritePermission" cols="2">
                        <v-btn
                          v-if="!pointCloud.intensityMin.editable"
                          text
                          color="success"
                          @click="pointCloud.intensityMin.editable = true"
                        >
                          EDIT
                        </v-btn>
                        <v-btn
                          v-else
                          :disabled="!pointCloud.intensityMin.valid"
                          text
                          @click="
                            updatePointCloud(
                              index,
                              'IntensityMin',
                              pointCloud.intensityMin
                            )
                          "
                        >
                          <v-icon color="success">mdi-check</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row class="mr-3">
                      <v-col cols="5">Intensity (max)</v-col>
                      <v-col cols="5">
                        <v-form
                          v-model="pointCloud.intensityMax.valid"
                          @submit.prevent
                        >
                          <v-text-field
                            :name="'intensity-max-' + index"
                            hide-details="auto"
                            :readonly="!pointCloud.intensityMax.editable"
                            v-model="pointCloud.intensityMax.value"
                            :rules="pointCloud.intensityMax.rules"
                            outlined
                            dense
                            max="15"
                          ></v-text-field>
                        </v-form>
                      </v-col>
                      <v-col v-if="hasWritePermission" cols="2">
                        <v-btn
                          v-if="!pointCloud.intensityMax.editable"
                          text
                          color="success"
                          @click="pointCloud.intensityMax.editable = true"
                        >
                          EDIT
                        </v-btn>
                        <v-btn
                          v-else
                          :disabled="!pointCloud.intensityMax.valid"
                          text
                          @click="
                            updatePointCloud(
                              index,
                              'IntensityMax',
                              pointCloud.intensityMax
                            )
                          "
                        >
                          <v-icon color="success">mdi-check</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row class="mr-3">
                      <v-col cols="5">Opacity</v-col>
                      <v-col cols="5">
                        <v-form
                          v-model="pointCloud.pointOpacity.valid"
                          @submit.prevent
                        >
                          <v-text-field
                            :name="'opacity-' + index"
                            hide-details="auto"
                            :readonly="!pointCloud.pointOpacity.editable"
                            v-model="pointCloud.pointOpacity.value"
                            :rules="pointCloud.pointOpacity.rules"
                            outlined
                            dense
                            max="15"
                          ></v-text-field>
                        </v-form>
                      </v-col>
                      <v-col v-if="hasWritePermission" cols="2">
                        <v-btn
                          v-if="!pointCloud.pointOpacity.editable"
                          text
                          color="success"
                          @click="pointCloud.pointOpacity.editable = true"
                        >
                          EDIT
                        </v-btn>
                        <v-btn
                          v-else
                          :disabled="!pointCloud.pointOpacity.valid"
                          text
                          @click="
                            updatePointCloud(
                              index,
                              'PointOpacity',
                              pointCloud.pointOpacity
                            )
                          "
                        >
                          <v-icon color="success">mdi-check</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col
                        v-if="
                          studio.permission === 'owner' ||
                            studio.permission === 'rw'
                        "
                        class="text-right"
                      >
                        <v-btn
                          v-if="!studio.shared && !studio.publicAccess"
                          text
                          color="error"
                          class="text-none"
                          @click="deletePointCloud(index)"
                        >
                          Remove
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-col>
            <v-col
              v-if="addingNewPointCloud"
              :cols="$vuetify.breakpoint.smAndUp ? 6 : 12"
            >
              <v-progress-circular
                class="ml-10 mt-10"
                indeterminate
                color="primary"
              />
            </v-col>
          </v-row>
          <v-row class="mt-12">
            <v-col cols="4">
              <v-btn
                color="error"
                class="text-none"
                @click="page_to('/studios/list')"
              >
                Back
              </v-btn>
            </v-col>
          </v-row>

          <!-- detail dialog -->
          <template v-if="detailDialog">
            <detail-dialog
              :detail-dialog="detailDialog"
              :current-studio="currentStudio"
              @setDetailDialog="setDetailDialog"
              @initStudio="initStudio"
            ></detail-dialog>
          </template>
        </template>
      </v-container>
    </v-flex>
    <!-- profile dialog -->
    <template v-show="profileDialog">
      <profile-dialog
        :profile-dialog="profileDialog"
        :profile="studio.ownerInfo"
        @setProfileDialog="setProfileDialog"
      ></profile-dialog>
    </template>
    <share-dialog
      v-if="studio.permission === 'owner' && initSharedDialogPrepared"
      :share-dialog="shareDialog"
      :type="'studio'"
      :id="studio.id"
      :is-public-before="studio.publicAccess"
      @setShareDialog="setShareDialog"
      @initParent="initStudio(false)"
    />
    <v-dialog
      v-if="selectBrainDialog"
      v-model="selectBrainDialog"
      max-width="800px"
    >
      <v-card>
        <list-main
          :type="'dialog_single_selectable'"
          :selectedBrains="pointClouds.length > 0 ? pointClouds : []"
          @setSelectedBrains="setSelectedBrains"
          @setSelectBrainDialog="setSelectBrainDialog"
        ></list-main>
      </v-card>
    </v-dialog>
    <confirm-dialog ref="confirm"></confirm-dialog>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import moment from 'moment'
import Vue from 'vue'

import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ListMain from '@/components/brain/ListMain.vue'
import ShareDialog from '@/components/ShareDialog.vue'
import DetailDialog from '@/components/studio/DetailDialog.vue'
import MarkdownItVue from 'markdown-it-vue'
import ProfileDialog from '@/components/account/ProfileDialog.vue'
import 'markdown-it-vue/dist/markdown-it-vue.css'

import 'viewerjs/dist/viewer.css'
import Viewer from 'v-viewer'
Vue.use(Viewer, {
  defaultOptions: {
    title: false,
    toolbar: false,
    zoomable: false,
    movable: false,
  },
})

export default {
  title: 'studio',
  components: {
    ConfirmDialog,
    ListMain,
    ShareDialog,
    DetailDialog,
    MarkdownItVue,
    ProfileDialog,
  },
  data: () => ({
    isNew: false,
    loading: true,
    addingNewPointCloud: false,
    selectBrainDialog: false,
    pointClouds: [],
    detailDialog: false,
    shareDialog: false,
    profileDialog: false,
    initing: false,
    initSharedDialogPrepared: false,
    studio: {
      id: null,
      ownerId: null,
      ownerInfo: null,
      title: {
        value: null,
        editable: false,
        rules: [
          (v) => !!v || 'Required.',
          (v) => !v || /^[ -~]*$/i.test(v) || 'Invalid characters.',
          (v) => !v || v.length <= 256 || 'Please enter within 256 characters.',
        ],
      },
      notes: {
        value: '# You can use markdown here.',
        editable: false,
        rules: [
          (v) => !v || /^[ -~\n\r]*$/i.test(v) || 'Invalid characters.',
          (v) =>
            !v || v.length <= 2048 || 'Please enter within 2048 characters.',
        ],
      },
      shared: false,
      // If owned, the permission is 'owner'.
      // If shared by another user, ther permission is 'r' or 'rw'.
      permission: 'owner',
      publicAccess: false,
      createdAtOrigin: null,
      createdAt: null,
      updatedAt: null,
    },
    brainDetailsNotAvailable: [],
    currentStudio: null,
  }),
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    hasWritePermission: function() {
      return (
        this.studio.permission === 'owner' || this.studio.permission === 'rw'
      )
    },
  },
  methods: {
    async archive() {
      if (
        !(await this.$refs.confirm.open(
          'Are you sure that you want to archive this studio?'
        ))
      ) {
        return
      }
      this.loading = true
      const result = await this.deleteStudio(this.studio.id)
      if (!result) {
        return
      }
      this.page_to('/studios/list')
    },
    async initStudio(full = true) {
      while (true) {
        if (!this.initing) {
          break
        }
        await new Promise((resolve) => setTimeout(resolve, 2000))
      }
      if (full) {
        // Reset all conditions including dialog windows.
        Object.assign(this.$data, this.$options.data())
      } else {
        this.$_initBackground()
      }
      this.initing = true
      this.studio.id = this.studio.id || this.$route.params.studio_id || null
      if (!this.studio.id) {
        this.isNew = true
        const studio = await this.createStudio()
        this.studio.id = studio.data.ItemId
      }
      const studioDetail = await this.getStudioById(this.studio.id)
      if (!studioDetail) {
        // When archived etc...
        this.$router.push({name: 'studios_list'})
        return
      }
      this.studio.title.value = studioDetail.Title
      if (this.isNew) {
        // False is not reassigned to initSharedDialogPrepared
        // even if this.initNotebook(false) is called.
        this.initSharedDialogPrepared = true
        this.initing = false
        this.loading = false
        return
      }
      // Set existing data of the studio from here.
      this.studio.ownerId = studioDetail.OwnerId
      this.studio.ownerInfo = await this.getAccountById(studioDetail.OwnerId)
      this.studio.notes.value = studioDetail.Notes
      this.studio.shared = studioDetail.Shared
      this.studio.publicAccess = studioDetail.PublicAccess
      this.studio.createdAtOrigin = studioDetail.CreatedAt
      this.studio.createdAt = moment(studioDetail.CreatedAt).format(
        'YYYY/MM/DD'
      )
      this.studio.updatedAt = moment(studioDetail.UpdatedAt).format(
        'YYYY/MM/DD'
      )
      this.pointClouds = studioDetail.PointClouds.map((pointCloud) =>
        this.$_convertPointCloudObjectForDisplay(pointCloud)
      )
      // False is not reassigned to initSharedDialogPrepared
      // even if this.initNotebook(false) is called.
      this.initSharedDialogPrepared = true
      if (
        this.studio.publicAccess &&
        this.user.username !== this.studio.ownerId
      ) {
        this.studio.permission = 'r'
      }
      if (this.studio.shared) {
        const result = await this.getShared(this.studio.id, 'studio')
        const sharedUsers = result.data
        for (const sharedUser of sharedUsers) {
          if (this.user.attributes.email === sharedUser.Email) {
            this.studio.permission = sharedUser.Permission
          }
        }
      }
      // Check brains' consistency.
      const availableBrains = []
      // The list of brains archived or made unshared after creation of shared studio.
      let brainIdsNotAvailable = []
      for (const pointCloud of this.pointClouds) {
        const brainPermission = await this.getBrainPermission(
          pointCloud.brainId
        )
        if (!brainPermission.ReadData) {
          brainIdsNotAvailable.push(pointCloud.brainId)
        } else {
          const availableBrain = await this.getBrainById(pointCloud.brainId)
          availableBrains.push(availableBrain)
        }
      }
      // Delete dupulication.
      brainIdsNotAvailable = brainIdsNotAvailable.filter(function(x, i, self) {
        return self.indexOf(x) === i
      })
      this.pointClouds = this.pointClouds.filter(function(pointCloud) {
        return brainIdsNotAvailable.indexOf(pointCloud.brainId) === -1
      })
      if (brainIdsNotAvailable.length > 0) {
        for (const brainIdNotAvailable of brainIdsNotAvailable) {
          this.brainDetailsNotAvailable.push(
            await this.getDetailOfBrainNotAvailable(brainIdNotAvailable)
          )
        }
        this.studio.permission = 'r'
      }
      // Set thumbnail.
      if (!this.studio.thumbnail) {
        this.studio.thumbnail = await this.getStudioThumbnailById(
          this.studio.id
        )
      }
      this.initing = false
      this.loading = false
    },
    async showDetailDialog(studio) {
      if (!this.loading) {
        this.loading = true
        if (!studio.thumbnail) {
          const thumbnail = await this.getStudioThumbnailById(studio.id)
          if (!thumbnail) {
            // Do nothing.
          } else {
            studio.thumbnail = thumbnail
          }
        }
        this.loading = false
      }
      this.currentStudio = studio
      this.detailDialog = true
    },
    setDetailDialog(isShow) {
      this.detailDialog = isShow
    },
    setProfileDialog(isShow) {
      this.profileDialog = isShow
    },
    setSelectBrainDialog(show) {
      this.selectBrainDialog = show
    },
    setShareDialog(show) {
      this.shareDialog = show
    },
    async setSelectedBrains(brains) {
      // The array is passed but it contains only one brain.
      this.addingNewPointCloud = true
      const result = await this.patchPointCloudOfStudio(this.studio.id, -1, {
        BrainId: brains[0].id,
      })
      // console.log(result)
      this.pointClouds = result.data.PointClouds.map((pointCloud) =>
        this.$_convertPointCloudObjectForDisplay(pointCloud)
      )
      this.addingNewPointCloud = false
    },
    updatePointCloud(index, attributeName, attribute) {
      attribute.editable = false
      if (
        attributeName === 'IntensityMin' ||
        attributeName === 'IntensityMax'
      ) {
        this.patchPointCloudOfStudio(this.studio.id, index, {
          [attributeName]: Number(attribute.value),
        })
      } else if (attributeName === 'PointColor') {
        this.patchPointCloudOfStudio(this.studio.id, index, {
          [attributeName]: attribute.value.replace('#', ''),
        })
      } else {
        this.patchPointCloudOfStudio(this.studio.id, index, {
          [attributeName]: attribute.value,
        })
      }
    },
    deletePointCloud(index) {
      this.pointClouds.splice(index, 1)
      this.deletePointCloudOfStudio(this.studio.id, index)
    },
    updateStudioTitle() {
      this.studio.title.editable = false
      this.patchStudio(this.studio.id, 'Title', this.studio.title.value)
    },
    updateStudioNotes() {
      this.studio.notes.editable = false
      this.patchStudio(this.studio.id, 'Notes', this.studio.notes.value)
    },
    $_initBackground() {
      this.isNew = false
      this.loading = true
    },
    $_convertPointCloudObjectForDisplay(pointCloud) {
      return {
        title: pointCloud.Title,
        brainId: pointCloud.BrainId,
        pointColorType: {
          value: pointCloud.PointColorType,
          editable: false,
        },
        pointColor: {
          value: '#' + pointCloud.PointColor,
          editable: false,
        },
        intensityMin: {
          value: String(pointCloud.IntensityMin),
          editable: false,
          rules: [
            (v) => !!v || 'Required.',
            (v) =>
              !v ||
              /^([1-9]\d*|0)(\.\d+)?$/i.test(v) ||
              'Only positive number can be used.',
            (v) => !v || v.length <= 10 || 'Please enter within 10 digits.',
          ],
          valid: true,
        },
        intensityMax: {
          value: String(pointCloud.IntensityMax),
          editable: false,
          rules: [
            (v) => !!v || 'Required.',
            (v) =>
              !v ||
              /^([1-9]\d*|0)(\.\d+)?$/i.test(v) ||
              'Only positive number can be used.',
            (v) => !v || v.length <= 10 || 'Please enter within 10 digits.',
          ],
          valid: true,
        },
        pointOpacity: {
          value: String(pointCloud.PointOpacity),
          editable: false,
          rules: [
            (v) => !!v || 'Required.',
            (v) =>
              !v ||
              /^(0)(\.\d+)?$/i.test(v) ||
              v === '1' ||
              'Only 0 to 1 can be specified.',
            (v) => !v || v.length <= 10 || 'Please enter within 10 digits.',
          ],
          valid: true,
        },
      }
    },
  },
  mounted() {
    this.initStudio()
  },
}
</script>
<style scoped>
.plot-area-center {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
